import { packageLabel } from '@/lib/realEstatePackages';
import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ordersAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Plus, Package, Loader2, AlertCircle, Clock, CheckCircle, ArrowRight, Send, XCircle, Camera } from 'lucide-react';

// Service type labels
const SERVICE_TYPE_LABELS = {
  photo_restoration: 'Photo Restoration',
  lora_training: 'LoRA Training',
  photo_scanning: 'Photo Scanning',
  digital_persona: 'Digital Persona',
  general_upload: 'General Service',
  real_estate: 'Real Estate',
  vacant_land: 'Vacant Land',
};

// Status badge config - matches backend OrderStatus enum
const STATUS_CONFIG = {
  draft: { label: 'Draft', className: 'bg-gray-500/20 text-gray-400 border-gray-500/30' },
  received: { label: 'Received', className: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
  in_progress: { label: 'In Progress', className: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
  delivered: { label: 'Delivered', className: 'bg-green-500/20 text-green-400 border-green-500/30' },
  canceled: { label: 'Canceled', className: 'bg-red-500/20 text-red-400 border-red-500/30' },
  // Real estate specific statuses
  requested: { label: 'Requested', className: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
  scheduled: { label: 'Scheduled', className: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
  shot: { label: 'Shot Complete', className: 'bg-purple-500/20 text-purple-400 border-purple-500/30' },
  editing: { label: 'Editing', className: 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30' },
  paid: { label: 'Paid', className: 'bg-green-500/20 text-green-400 border-green-500/30' },
  // Legacy statuses for backward compatibility
  pending: { label: 'Pending', className: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
  review: { label: 'In Review', className: 'bg-purple-500/20 text-purple-400 border-purple-500/30' },
  completed: { label: 'Completed', className: 'bg-green-500/20 text-green-400 border-green-500/30' },
  cancelled: { label: 'Cancelled', className: 'bg-red-500/20 text-red-400 border-red-500/30' },
  on_hold: { label: 'On Hold', className: 'bg-orange-500/20 text-orange-400 border-orange-500/30' },
};

// Tab categories - include real estate statuses
// Paid+Delivered orders should show as Completed
const ACTIVE_STATUSES = ['draft', 'received', 'in_progress', 'pending', 'review', 'on_hold', 'requested', 'scheduled', 'shot', 'editing'];
const COMPLETED_STATUSES = ['delivered', 'completed', 'paid'];
const CANCELED_STATUSES = ['canceled', 'cancelled'];

// Helper to determine if order should be treated as completed
function isOrderCompleted(order) {
  // Order is completed if status is in COMPLETED_STATUSES
  // This includes: delivered, completed, paid
  return COMPLETED_STATUSES.includes(order.status);
}

// Helper to determine if order is active
function isOrderActive(order) {
  if (isOrderCompleted(order)) return false;
  if (CANCELED_STATUSES.includes(order.status)) return false;
  return true;
}

function StatusBadge({ status }) {
  const config = STATUS_CONFIG[status] || { label: status, className: 'bg-gray-500/20 text-gray-400 border-gray-500/30' };
  
  return (
    <Badge className={config.className}>
      {config.label}
    </Badge>
  );
}

function IntakeBadge({ intakeComplete }) {
  return (
    <Badge className={intakeComplete 
      ? 'bg-green-500/20 text-green-400 border-green-500/30' 
      : 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'
    }>
      {intakeComplete ? 'Intake Complete' : 'Intake Pending'}
    </Badge>
  );
}

function formatDate(dateString) {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

// Generate readable order number from UUID
function formatOrderNumber(orderId, serviceType) {
  if (!orderId) return 'N/A';
  // Use first 8 chars of UUID for a short ID
  const shortId = orderId.split('-')[0].toUpperCase();
  const prefix = serviceType === 'real_estate' ? 'RE' : 'ORD';
  return `${prefix}-${shortId}`;
}

// Format price for display
function formatPrice(cents, isCustomQuote) {
  if (isCustomQuote) return 'Custom Quote';
  if (!cents) return null;
  return `$${(cents / 100).toFixed(0)}`;
}

function OrderCard({ order }) {
  const serviceLabel = SERVICE_TYPE_LABELS[order.service_type] || order.service_type;
  const isRealEstate = order.service_type === 'real_estate';
  const details = order.details || {};
  
  // Get display info for real estate orders
  const orderNumber = formatOrderNumber(order.id, order.service_type);
  
  // For real estate: use invoice total if available, else details.total_price_cents
  // This ensures we show the actual billed amount, not just the package base price
  const price = isRealEstate 
    ? formatPrice(order.invoice_total_cents || details.total_price_cents, details.is_custom_quote) 
    : null;
  
  const preferredDate = isRealEstate && details.preferred_date_1_date 
    ? formatDate(details.preferred_date_1_date + 'T00:00:00')
    : null;
  
  // Format package name nicely
  const packageName = details.package ? packageLabel(details.package) : null;

  return (
    <Link to={`/orders/${order.id}`} className="block group" data-testid={`order-card-${order.id}`}>
      <Card className="bg-[#0f1724] border-white/10 hover:border-[#C6A14A]/50 transition-all duration-300 group-hover:shadow-lg group-hover:shadow-[#C6A14A]/5">
        <CardHeader className="pb-2">
          <div className="flex items-start justify-between gap-2">
            <div className="space-y-1 min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <CardTitle className="text-lg text-white group-hover:text-[#C6A14A] transition-colors truncate">
                  {order.title || serviceLabel}
                </CardTitle>
                <span className="text-xs text-[#C6A14A] font-mono bg-[#C6A14A]/10 px-2 py-0.5 rounded">
                  {orderNumber}
                </span>
              </div>
              <CardDescription className="text-[#E8E4DE]/60">
                {serviceLabel}
                {isRealEstate && packageName && (
                  <span className="ml-2 text-[#C6A14A]/70">
                    • {packageName}
                  </span>
                )}
              </CardDescription>
            </div>
            <div className="flex flex-col items-end gap-1">
              <StatusBadge status={order.status} />
              {!isRealEstate && <IntakeBadge intakeComplete={order.intake_complete} />}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between text-sm">
            <div className="space-y-1 text-[#E8E4DE]/40">
              {isRealEstate ? (
                <>
                  {preferredDate && (
                    <div className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      <span>Preferred: {preferredDate}</span>
                    </div>
                  )}
                  {price && (
                    <div className="flex items-center gap-1 text-[#C6A14A]/60">
                      <Package className="h-3 w-3" />
                      <span>{price}</span>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    <span>Created {formatDate(order.created_at)}</span>
                  </div>
                  {order.submitted_at && (
                    <div className="flex items-center gap-1 text-[#C6A14A]/60">
                      <Send className="h-3 w-3" />
                      <span>Submitted {formatDate(order.submitted_at)}</span>
                    </div>
                  )}
                </>
              )}
            </div>
            <ArrowRight className="h-4 w-4 text-[#C6A14A] opacity-0 group-hover:opacity-100 transition-opacity" />
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

function OrdersGrid({ orders, emptyMessage }) {
  if (orders.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <Package className="h-10 w-10 text-[#E8E4DE]/30 mb-4" />
        <p className="text-[#E8E4DE]/60">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
      {orders.map((order) => (
        <OrderCard key={order.id} order={order} />
      ))}
    </div>
  );
}

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('active');

  useEffect(() => {
    loadOrders();
  }, []);

  const loadOrders = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await ordersAPI.list();
      setOrders(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Filter and sort orders by tab using helper functions
  const { activeOrders, completedOrders, canceledOrders } = useMemo(() => {
    const sortByDate = (a, b) => {
      // Sort by submitted_at if available, otherwise by created_at (most recent first)
      const dateA = a.submitted_at || a.created_at;
      const dateB = b.submitted_at || b.created_at;
      return new Date(dateB) - new Date(dateA);
    };

    const active = orders
      .filter(order => isOrderActive(order))
      .sort(sortByDate);
    
    const completed = orders
      .filter(order => isOrderCompleted(order))
      .sort(sortByDate);
    
    const canceled = orders
      .filter(order => CANCELED_STATUSES.includes(order.status))
      .sort(sortByDate);

    return { activeOrders: active, completedOrders: completed, canceledOrders: canceled };
  }, [orders]);

  return (
    <div className="min-h-screen bg-[#050816]">
      {/* Header */}
      <div className="bg-[#0f1724] border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 py-8 md:py-12">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-light text-white tracking-wide">My Orders</h1>
              <p className="text-[#E8E4DE]/60 mt-1 text-sm md:text-base">Manage your project orders and uploads</p>
            </div>
            <Button asChild className="bg-[#C6A14A] text-[#050816] hover:bg-[#D4AF5A] font-medium h-11" data-testid="header-new-order-btn">
              <Link to="/book">
                <Plus className="mr-2 h-4 w-4" />
                New order
              </Link>
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 text-[#C6A14A] animate-spin" />
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <AlertCircle className="h-12 w-12 text-red-400 mb-4" />
            <h3 className="text-lg text-white mb-2">Failed to load orders</h3>
            <p className="text-[#E8E4DE]/60 mb-4">{error}</p>
            <Button onClick={loadOrders} variant="outline" className="border-white/10 text-[#E8E4DE]">
              Try Again
            </Button>
          </div>
        )}

        {/* Empty State - No orders at all */}
        {!loading && !error && orders.length === 0 && (
          <div className="flex flex-col items-center justify-center py-20 text-center" data-testid="empty-orders-state">
            <div className="w-24 h-24 bg-[#C6A14A]/10 rounded-full flex items-center justify-center mb-6">
              <Camera className="h-12 w-12 text-[#C6A14A]" />
            </div>
            <h3 className="text-2xl text-white mb-3">No orders yet</h3>
            <p className="text-[#E8E4DE]/60 mb-8 max-w-md">
              Ready to capture your property at its best? Book a real estate photography shoot or explore our other creative services.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                asChild
                size="lg"
                className="bg-[#C6A14A] text-[#050816] hover:bg-[#D4AF5A] px-8"
                data-testid="empty-state-book-shoot-btn"
              >
                <Link to="/book">
                  <Camera className="mr-2 h-5 w-5" />
                  Book a Shoot
                </Link>
              </Button>
              <Button 
                asChild
                variant="outline"
                size="lg"
                className="border-white/10 text-[#E8E4DE] hover:bg-white/5"
                data-testid="empty-state-other-services-btn"
              >
                <Link to="/book?type=other">
                  Other Services
                </Link>
              </Button>
            </div>
          </div>
        )}

        {/* Tabbed Orders View */}
        {!loading && !error && orders.length > 0 && (
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="bg-[#0f1724] border border-white/10 p-1" data-testid="orders-tabs">
              <TabsTrigger 
                value="active" 
                className="data-[state=active]:bg-[#C6A14A] data-[state=active]:text-[#050816] text-[#E8E4DE]/60 px-4"
                data-testid="tab-active"
              >
                <Clock className="h-4 w-4 mr-2" />
                Active ({activeOrders.length})
              </TabsTrigger>
              <TabsTrigger 
                value="completed" 
                className="data-[state=active]:bg-[#C6A14A] data-[state=active]:text-[#050816] text-[#E8E4DE]/60 px-4"
                data-testid="tab-completed"
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Completed ({completedOrders.length})
              </TabsTrigger>
              <TabsTrigger 
                value="canceled" 
                className="data-[state=active]:bg-[#C6A14A] data-[state=active]:text-[#050816] text-[#E8E4DE]/60 px-4"
                data-testid="tab-canceled"
              >
                <XCircle className="h-4 w-4 mr-2" />
                Canceled ({canceledOrders.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="active" data-testid="tab-content-active">
              <OrdersGrid 
                orders={activeOrders} 
                emptyMessage="No active orders. Create a new order to get started!" 
              />
            </TabsContent>

            <TabsContent value="completed" data-testid="tab-content-completed">
              <OrdersGrid 
                orders={completedOrders} 
                emptyMessage="No completed orders yet." 
              />
            </TabsContent>

            <TabsContent value="canceled" data-testid="tab-content-canceled">
              <OrdersGrid 
                orders={canceledOrders} 
                emptyMessage="No canceled orders." 
              />
            </TabsContent>
          </Tabs>
        )}
      </div>
    </div>
  );
}
